import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchProjectBySlug, fetchProjectLikes, toggleProjectLike, fetchProjectFeedbacks, submitProjectFeedback, hasUserLikedProject } from '../services/supabaseService';
import { useLanguage } from '../context/LanguageContext';
import { useTransition } from '../context/TransitionContext';
import { getProjectFromSubdomain, getProjectUrl, slugify } from '../utils/subdomain';
import { devError } from '../utils/devLog';
import ShareButton from '../components/ShareButton';

// Vivid hero palette. The projects table has no color column, so we derive a
// stable per-project color (same project → same color on every visit).
const HERO_PALETTE = ['#FF4500', '#9ACD32', '#3D2FA9', '#FF7722', '#1A7DB7', '#E94C8A', '#16A34A', '#F59E0B'];

const pickHeroColor = (project) => {
  const key = String(project?.id ?? project?.title ?? '');
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return HERO_PALETTE[hash % HERO_PALETTE.length];
};

// Perceived luminance → choose readable text color on the hero background
const isLightColor = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
};

const ProjectDetail = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug || getProjectFromSubdomain();
  const { startTransition } = useTransition();
  const { t } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', message: '', rating: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [likePending, setLikePending] = useState(false);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const data = await fetchProjectBySlug(slug);
        if (data) {
          setProject(data);
          const likesCount = await fetchProjectLikes(data.id);
          setLikes(likesCount);
          const liked = await hasUserLikedProject(data.id);
          setIsLiked(liked);
          const feedbacksData = await fetchProjectFeedbacks(data.id);
          setFeedbacks(feedbacksData);
        }
      } catch (err) {
        devError('Error loading project:', err);
      } finally {
        setLoading(false);
      }
    };
    loadProject();
  }, [slug]);

  const handleLike = async () => {
    if (likePending || !project) return;
    setLikePending(true);

    const wasLiked = isLiked;
    const next = !wasLiked;

    // Optimistic update
    setIsLiked(next);
    setLikes(prev => Math.max(0, next ? prev + 1 : prev - 1));

    const success = await toggleProjectLike(project.id, wasLiked);
    if (!success) {
      // Revert on failure
      setIsLiked(wasLiked);
      setLikes(prev => Math.max(0, next ? prev - 1 : prev + 1));
    }
    setLikePending(false);
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!project || !feedbackForm.name || !feedbackForm.message) return;
    setSubmitting(true);
    const success = await submitProjectFeedback(project.id, feedbackForm);
    if (success) {
      setFeedbackForm({ name: '', email: '', message: '', rating: 5 });
      const updatedFeedbacks = await fetchProjectFeedbacks(project.id);
      setFeedbacks(updatedFeedbacks);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#FBF9EF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ 
          width: '48px', 
          height: '48px', 
          border: '3px solid #E2DFDD', 
          borderTopColor: '#FF7722',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#FBF9EF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        padding: '20px'
      }}>
        <h1 style={{ 
          fontFamily: "'Gellix', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 'clamp(48px, 10vw, 96px)', 
          fontWeight: 700, 
          color: '#171412',
          textAlign: 'center'
        }}>
          404
        </h1>
        <p style={{
          fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
          fontSize: '18px',
          color: '#8E827C',
          textAlign: 'center'
        }}>
          {t('project.notFound') || 'Project not found'}
        </p>
        <button 
          onClick={() => startTransition('/work')}
          style={{
            padding: '16px 32px',
            backgroundColor: '#171412',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
            fontSize: '16px',
            fontWeight: 400,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3D2FA9'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#171412'}
        >
          {t('project.backToWork') || 'Back to Work'}
        </button>
      </div>
    );
  }

  // Honor a per-project color if one is set in the DB, else derive a stable one
  const heroColor = (typeof project.color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(project.color))
    ? project.color
    : pickHeroColor(project);
  const lightHero = isLightColor(heroColor);
  const heroText = lightHero ? '#171412' : '#FFFFFF';
  const heroMuted = lightHero ? 'rgba(23, 20, 18, 0.65)' : 'rgba(255, 255, 255, 0.8)';
  const heroGlassBg = lightHero ? 'rgba(23, 20, 18, 0.08)' : 'rgba(255, 255, 255, 0.15)';
  const heroBorder = lightHero ? 'rgba(23, 20, 18, 0.25)' : 'rgba(255, 255, 255, 0.35)';

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FBF9EF',
      paddingBottom: '96px'
    }}>
      {/* Header removed as requested */}

      {/* Hero Section — dynamic full-bleed color per project */}
      <section style={{ backgroundColor: heroColor, color: heroText, transition: 'background-color 0.4s ease', padding: '72px 64px 96px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Meta Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {project.category && (
            <span style={{
              padding: '6px 16px',
              borderRadius: '6px',
              backgroundColor: heroGlassBg,
              color: heroText,
              fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              border: `1px solid ${heroBorder}`
            }}>
              {project.category}
            </span>
          )}
          {Array.isArray(project.platform) && project.platform.length > 0 && (
            <span style={{
              padding: '6px 16px',
              borderRadius: '6px',
              backgroundColor: heroGlassBg,
              color: heroText,
              fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              border: `1px solid ${heroBorder}`
            }}>
              {project.platform.join(' / ')}
            </span>
          )}
          {project.year && (
            <span style={{
              fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              fontSize: '11px',
              fontWeight: 700,
              color: heroMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {project.year}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: "'Gellix', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 'clamp(48px, 8vw, 96px)',
          fontWeight: 700,
          color: heroText,
          lineHeight: 1.1,
          marginBottom: '24px',
          letterSpacing: '-0.02em'
        }}>
          {project.title}
        </h1>

        {/* Description */}
        <p style={{
          fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
          fontSize: '20px',
          fontWeight: 400,
          color: heroMuted,
          lineHeight: 1.6,
          maxWidth: '640px',
          marginBottom: '0'
        }}>
          {project.description}
        </p>
        </div>
      </section>

      {/* Hero Image */}
      <section style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 64px' }}>
        <div style={{
          width: '100%',
          height: 'clamp(300px, 50vw, 600px)',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#F2F0E7'
        }}>
          <img 
            src={project.image || `https://via.placeholder.com/1400x600/F2F0E7/8E827C?text=${encodeURIComponent(project.title)}`}
            alt={project.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      </section>

      {/* Content */}
      <section style={{ maxWidth: '1400px', margin: '0 auto', padding: '96px 64px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '64px', alignItems: 'start' }}>
          {/* Main Content */}
          <div>
            {/* Tech Stack */}
            {project.tech && project.tech.length > 0 && (
              <div style={{ marginBottom: '48px' }}>
                <h3 style={{
                  fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#8E827C',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Tech Stack
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {project.tech.map((tech, index) => (
                    <span key={index} style={{
                      padding: '10px 20px',
                      borderRadius: '12px',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E2DFDD',
                      fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
                      fontSize: '14px',
                      fontWeight: 400,
                      color: '#171412',
                      transition: 'all 0.2s ease'
                    }}>
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Project Details Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '32px',
              marginBottom: '48px'
            }}>
              {project.status && (
                <div style={{
                  padding: '24px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E2DFDD'
                }}>
                  <div style={{ 
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", 
                    fontSize: '11px', 
                    fontWeight: 700,
                    color: '#8E827C', 
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Status
                  </div>
                  <div style={{ 
                    fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", 
                    fontSize: '16px', 
                    fontWeight: 400, 
                    color: '#171412',
                    textTransform: 'capitalize'
                  }}>
                    {project.status}
                  </div>
                </div>
              )}
              {project.category && (
                <div style={{
                  padding: '24px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E2DFDD'
                }}>
                  <div style={{ 
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", 
                    fontSize: '11px', 
                    fontWeight: 700,
                    color: '#8E827C', 
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Category
                  </div>
                  <div style={{ 
                    fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", 
                    fontSize: '16px', 
                    fontWeight: 400, 
                    color: '#171412',
                    textTransform: 'capitalize'
                  }}>
                    {project.category}
                  </div>
                </div>
              )}
              {Array.isArray(project.platform) && project.platform.length > 0 && (
                <div style={{
                  padding: '24px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E2DFDD'
                }}>
                  <div style={{ 
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", 
                    fontSize: '11px', 
                    fontWeight: 700,
                    color: '#8E827C', 
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Platform
                  </div>
                  <div style={{ 
                    fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", 
                    fontSize: '16px', 
                    fontWeight: 400, 
                    color: '#171412'
                  }}>
                    {project.platform.join(', ')}
                  </div>
                </div>
              )}
              {project.created_at && (
                <div style={{
                  padding: '24px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E2DFDD'
                }}>
                  <div style={{ 
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", 
                    fontSize: '11px', 
                    fontWeight: 700,
                    color: '#8E827C', 
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Created
                  </div>
                  <div style={{ 
                    fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", 
                    fontSize: '16px', 
                    fontWeight: 400, 
                    color: '#171412'
                  }}>
                    {new Date(project.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ position: 'sticky', top: '100px' }}>
            {/* Like Card */}
            <div style={{
              padding: '32px',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E2DFDD',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <div style={{ 
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", 
                    fontSize: '11px', 
                    fontWeight: 700,
                    color: '#8E827C',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '4px'
                  }}>
                    Likes
                  </div>
                  <div style={{ 
                    fontFamily: "'Gellix', -apple-system, BlinkMacSystemFont, sans-serif", 
                    fontSize: '32px', 
                    fontWeight: 700, 
                    color: '#171412' 
                  }}>
                    {likes}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", 
                    fontSize: '11px', 
                    fontWeight: 700,
                    color: '#8E827C',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '4px'
                  }}>
                    Feedback
                  </div>
                  <div style={{ 
                    fontFamily: "'Gellix', -apple-system, BlinkMacSystemFont, sans-serif", 
                    fontSize: '32px', 
                    fontWeight: 700, 
                    color: '#171412' 
                  }}>
                    {feedbacks.length}
                  </div>
                </div>
              </div>
              <button
                onClick={handleLike}
                disabled={likePending}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '12px',
                  border: isLiked ? 'none' : '1px solid #E2DFDD',
                  backgroundColor: isLiked ? '#FF7722' : '#FFFFFF',
                  color: isLiked ? '#FFFFFF' : '#171412',
                  cursor: likePending ? 'not-allowed' : 'pointer',
                  fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
                  fontSize: '14px',
                  fontWeight: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isLiked) {
                    e.currentTarget.style.borderColor = '#807E7D';
                    e.currentTarget.style.boxShadow = '0px 2px 8px rgba(0, 0, 0, 0.04)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLiked) {
                    e.currentTarget.style.borderColor = '#E2DFDD';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {isLiked ? '❤️' : '🤍'} {isLiked ? 'Liked' : 'Like'}
              </button>
            </div>

            {/* Quick Info */}
            <div style={{
              padding: '24px',
              backgroundColor: '#F2F0E7',
              borderRadius: '12px',
              border: '2px solid #FF7722'
            }}>
              <h4 style={{
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                fontSize: '11px',
                fontWeight: 700,
                color: '#171412',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Quick Info
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {project.tech && project.tech.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", fontSize: '14px', color: '#8E827C' }}>
                      Technologies
                    </span>
                    <span style={{ fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", fontSize: '14px', color: '#171412', fontWeight: 500 }}>
                      {project.tech.length}
                    </span>
                  </div>
                )}
                {Array.isArray(project.platform) && project.platform.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", fontSize: '14px', color: '#8E827C' }}>
                      Platforms
                    </span>
                    <span style={{ fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", fontSize: '14px', color: '#171412', fontWeight: 500 }}>
                      {project.platform.length}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feedback Section */}
      <section style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: '0 64px',
        borderTop: '1px solid #E2DFDD',
        paddingTop: '96px'
      }}>
        <h2 style={{
          fontFamily: "'Gellix', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 700,
          color: '#171412',
          marginBottom: '48px'
        }}>
          Feedback
        </h2>

        {/* Feedback Form */}
        <form onSubmit={handleFeedbackSubmit} style={{
          padding: '40px',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E2DFDD',
          marginBottom: '48px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            <div>
              <label style={{
                display: 'block',
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                fontSize: '11px',
                fontWeight: 700,
                color: '#171412',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Name *
              </label>
              <input
                type="text"
                value={feedbackForm.name}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #E2DFDD',
                  fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
                  fontSize: '16px',
                  color: '#171412',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#FF7722';
                  e.target.style.boxShadow = '0px 0px 0px 3px rgba(255, 119, 34, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E2DFDD';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                fontSize: '11px',
                fontWeight: 700,
                color: '#171412',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Email
              </label>
              <input
                type="email"
                value={feedbackForm.email}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #E2DFDD',
                  fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
                  fontSize: '16px',
                  color: '#171412',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#FF7722';
                  e.target.style.boxShadow = '0px 0px 0px 3px rgba(255, 119, 34, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E2DFDD';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              fontSize: '11px',
              fontWeight: 700,
              color: '#171412',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Message *
            </label>
            <textarea
              value={feedbackForm.message}
              onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
              required
              rows={4}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #E2DFDD',
                fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
                fontSize: '16px',
                color: '#171412',
                resize: 'vertical',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#FF7722';
                e.target.style.boxShadow = '0px 0px 0px 3px rgba(255, 119, 34, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E2DFDD';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              fontSize: '11px',
              fontWeight: 700,
              color: '#171412',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Rating
            </label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFeedbackForm({ ...feedbackForm, rating: star })}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '28px',
                    color: star <= feedbackForm.rating ? '#FF7722' : '#E2DFDD',
                    transition: 'color 0.2s ease',
                    padding: '0'
                  }}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '16px 32px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: '#FF7722',
              color: '#FFFFFF',
              fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
              fontSize: '16px',
              fontWeight: 400,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
              boxShadow: '0px 4px 12px rgba(255, 119, 34, 0.2)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!submitting) {
                e.currentTarget.style.backgroundColor = '#FF592C';
                e.currentTarget.style.boxShadow = '0px 6px 16px rgba(255, 89, 44, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (!submitting) {
                e.currentTarget.style.backgroundColor = '#FF7722';
                e.currentTarget.style.boxShadow = '0px 4px 12px rgba(255, 119, 34, 0.2)';
              }
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>

        {/* Feedback List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {feedbacks.length === 0 ? (
            <div style={{
              padding: '48px',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E2DFDD',
              textAlign: 'center'
            }}>
              <p style={{
                fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
                fontSize: '16px',
                color: '#8E827C',
                margin: 0
              }}>
                No feedback yet. Be the first to share your thoughts!
              </p>
            </div>
          ) : (
            feedbacks.map((fb) => (
              <div key={fb.id} style={{
                padding: '32px',
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                border: '1px solid #E2DFDD'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ 
                    fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", 
                    fontSize: '16px',
                    fontWeight: 500, 
                    color: '#171412' 
                  }}>
                    {fb.name}
                  </div>
                  <div style={{ 
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", 
                    fontSize: '11px', 
                    color: '#8E827C' 
                  }}>
                    {new Date(fb.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ color: '#FF7722', marginBottom: '12px', fontSize: '18px' }}>
                  {'★'.repeat(fb.rating || 5)}{'☆'.repeat(5 - (fb.rating || 5))}
                </div>
                <p style={{ 
                  fontFamily: "'Gellix', 'Helvetica Neue', sans-serif", 
                  fontSize: '16px', 
                  color: '#8E827C', 
                  lineHeight: 1.6,
                  margin: 0
                }}>
                  {fb.message}
                </p>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
